/**
 * WebSocket Plugin for Vue
 * Provides global WebSocket functionality across the application
 */

import type { App } from 'vue'
import { globalWebSocket } from '@/composables/useGlobalWebSocket'
import { useAuthStore } from '@/stores/auth'

export default {
  install(app: App) {
    // Provide global WebSocket instance
    app.provide('websocket', globalWebSocket)

    // Add global properties
    app.config.globalProperties.$websocket = globalWebSocket

    // Auto-connect when user is authenticated
    const authStore = useAuthStore()

    // Immediate connect if already authenticated on app load
    if (authStore.isAuthenticated && authStore.user && authStore.organizationId && authStore.token) {
      globalWebSocket.connect(authStore.organizationId, authStore.token)
    }

    // Watch for authentication changes
    const unwatch = authStore.$subscribe((mutation, state) => {
      if (authStore.isAuthenticated && state.user && state.organizationId) {
        // User is authenticated, connect to WebSocket
        if (state.token) {
          globalWebSocket.connect(state.organizationId, state.token)
        }
      } else {
        // User is not authenticated, disconnect
        globalWebSocket.disconnect()
      }
    })

    // Cleanup on app unmount
    app.config.globalProperties.$websocketUnwatch = unwatch
  },
}

// Export types for TypeScript support
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $websocket: typeof globalWebSocket
  }
}
